import { ComponentType, PropsWithChildren, useCallback } from 'react';

import { CountryEntity, PermissionType } from 'api/types/entity';
import { ContentHeader, SearchBarWithFilter } from 'components';
import { useTranslation } from 'libs/i18n';
import { PermissionWrapper } from 'libs/permissions';
import { Button, Stack } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import {
  PersonAndLegalClientsToggleButton,
  PersonAndLegalClientsToggleButtonProps,
} from 'modules/client/common/components';

type FilterType = {
  search?: string;
  [key: string]: unknown;
};

export interface ClientsOrLegalClientsViewProps<T extends FilterType>
  extends PropsWithChildren {
  filter: T;
  setFilter: (filter?: T | undefined) => void;
  registrationCountries?: CountryEntity[];
  locationCountries?: CountryEntity[];

  toggleButtonValue: PersonAndLegalClientsToggleButtonProps['value'];

  FilterComponent: ComponentType<{
    filter: Omit<T, 'search'>;
    locationCountries?: CountryEntity[];
    registrationCountries?: CountryEntity[];
    onApply: (filter: T) => void;
  }>;

  onClickAdd: () => void;
}

export const ClientsOrLegalClientsView = <T extends FilterType>({
  setFilter,
  filter,
  registrationCountries,
  locationCountries,
  children,
  toggleButtonValue,
  FilterComponent,
  onClickAdd,
}: ClientsOrLegalClientsViewProps<T>) => {
  const { t } = useTranslation();

  const { search, ...restFilter } = filter;

  const handleApply = useCallback(
    (newFilter: T) => {
      setFilter({ search, ...newFilter });
    },
    [search, setFilter]
  );

  return (
    <Stack spacing={24}>
      <ContentHeader
        header={t('client.clients.personal.title')}
        rightContent={
          <SearchBarWithFilter
            filterContent={
              <>
                <FilterComponent
                  filter={restFilter}
                  locationCountries={locationCountries}
                  registrationCountries={registrationCountries}
                  onApply={handleApply}
                />
                <PermissionWrapper
                  permissions={[PermissionType.Client_LegalClient_CreateOne]}
                >
                  <Button
                    fullWidth={false}
                    label={t('common.add')}
                    sizeVariant="s"
                    startIcon={<Icon.Plus size={16} />}
                    onClick={onClickAdd}
                  />
                </PermissionWrapper>
              </>
            }
            search={search ?? ''}
            onSearch={(v) => {
              setFilter({ ...restFilter, search: v } as T);
            }}
          />
        }
      />
      <Stack spacing={12}>
        <PersonAndLegalClientsToggleButton value={toggleButtonValue} />
        {children}
      </Stack>
    </Stack>
  );
};
